import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import appCssInline from "../styles.css?inline";

// Em produção o CSS é embutido no HTML (evita requisição que bloqueia a
// renderização e reduz o atraso do LCP). Em dev usamos o <link> para manter HMR.
const INLINE_CSS = import.meta.env.PROD ? (appCssInline as string) : "";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Toaster } from "@/components/ui/sonner";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "TrajetoFácil — roteirizador grátis" },
      {
        name: "description",
        content:
          "Roteirizador grátis para calcular rota, pedágio e combustível de viagens no Brasil, sem cadastro.",
      },
      { name: "author", content: "TrajetoFácil" },
      // Verificação de propriedade no Google Search Console
      {
        name: "google-site-verification",
        content: "N1hDi2Nsi2Pi1__K-a1tH4uOO7JO3wdBw0FERqUfUqM",
      },
      { property: "og:site_name", content: "TrajetoFácil" },
      { property: "og:title", content: "TrajetoFácil — roteirizador grátis" },
      {
        property: "og:description",
        content:
          "Roteirizador grátis para calcular rota, pedágio e combustível de viagens no Brasil, sem cadastro.",
      },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "pt_BR" },

      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      ...(INLINE_CSS
        ? []
        : [
            {
              rel: "stylesheet",
              href: appCss,
            },
          ]),
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/logo-trajetofacil.png" },
      // Pré-conexão com as origens de terceiros usadas logo após o carregamento
      { rel: "preconnect", href: "https://www.googletagmanager.com" },
      { rel: "dns-prefetch", href: "https://tile.openstreetmap.org" },
      { rel: "dns-prefetch", href: "https://pagead2.googlesyndication.com" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "TrajetoFácil",
          url: "https://www.trajetofacil.com.br",
          logo: "https://www.trajetofacil.com.br/logo-trajetofacil.png",
          description:
            "Roteirizador grátis de rota, pedágio e combustível para viagens no Brasil.",
        }),
      },
      // Google Analytics (gtag.js) carregado diretamente no <head>
      {
        src: "https://www.googletagmanager.com/gtag/js?id=G-HMJE0Q8Q0N",
        async: true,
      },
      {
        children: `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', 'G-HMJE0Q8Q0N');`,
      },
      // Google AdSense oficial no <head> (exigido pelo Google para verificação
      // e anúncios automáticos). É async, então não bloqueia a renderização;
      // o componente AdSlot apenas "empurra" os blocos quando visíveis.
      {
        src: "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-5354419908808807",
        async: true,
        crossOrigin: "anonymous",
      },
    ],

  }),

  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="pt-BR">
      <head>
        <HeadContent />
        {INLINE_CSS ? (
          <style
            id="critical-css"
            dangerouslySetInnerHTML={{ __html: INLINE_CSS }}
          />
        ) : null}
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
      <Toaster position="top-center" richColors />
    </QueryClientProvider>
  );
}
